import React, { useState, useEffect } from 'react'; // ⬅️ add useEffect
import { FaExclamationCircle } from 'react-icons/fa';
import './SignupLogin.css';
import { signUp, verifySignUp, login } from '../../api/userApi';
import Input from '../../components/Input/Input';
import googleIcon from '../../assets/icons8-google.svg';
import microsoftIcon from '../../assets/icons8-microsoft.svg';
import { useNavigate } from 'react-router-dom';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignUpLogin({ onGoogleSignIn, onMicrosoftSignIn, onSuccess }) {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(true);

  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    fetch('/api/config', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(setCfg)
      .catch(() => {});
  }, []);
  const signupsOpen = cfg?.signupsOpen ?? true; // if server exposes closed state, reflect it

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',

  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [serverInfo, setServerInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupVerificationPending, setSignupVerificationPending] = useState(false);
  const [signupCode, setSignupCode] = useState('');

  const setVal = (name, value) => {
    setFormData(f => ({ ...f, [name]: value }));
    setFieldErrors(e => ({ ...e, [name]: '' }));
    setServerError('');
    setServerInfo('');
  };

  const resetSignupVerification = () => {
    setSignupVerificationPending(false);
    setSignupCode('');
    setServerError('');
    setServerInfo('');
    setFieldErrors({});
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const code = p.get('code');
    if (code) setVal('accessCode', code);
  }, []);
  

  const validate = () => {
    const errs = {};
    const { email, firstName, lastName, password, confirmPassword} = formData;

    if (!EMAIL_RE.test((email || '').trim())) errs.email = 'Please enter a valid email address.';
    if (isSignUp) {
      if (signupVerificationPending) {
        if (!signupCode.trim()) errs.signupCode = 'Enter the verification code from your email.';
        return errs;
      }
      if (!firstName.trim()) errs.firstName = 'First name is required.';
      else if (firstName.trim().length > 50) errs.firstName = 'Max 50 characters.';
      if (!lastName.trim()) errs.lastName = 'Last name is required.';
      else if (lastName.trim().length > 50) errs.lastName = 'Max 50 characters.';
      if (!password) {
        errs.password = 'Password is required.';
      } else if (password.length < 8) {
        errs.password = 'Password must be at least 8 characters.';
      } else if (!/[A-Z]/.test(password)) {
        errs.password = 'Password must include at least one uppercase letter.';
      } else if (!/[0-9]/.test(password)) {
        errs.password = 'Password must include at least one number.';
      }
      if ((confirmPassword || '') !== (password || '')) errs.confirmPassword = 'Passwords do not match.';
    } else {
      if (!password) errs.password = 'Password is required.';
    }
    return errs;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setServerError('');
    setServerInfo('');

    if (isSignUp && cfg && !signupsOpen) {
      setServerError('Sign-ups are currently closed.');
      return;
    }

    const errs = validate();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }

    setLoading(true);
    try {
      const { email, firstName, lastName, password} = formData;
      if (isSignUp && signupVerificationPending) {
        const user = await verifySignUp({ email, code: signupCode.trim() });
        await onSuccess?.(user);
        return;
      }

      if (isSignUp) {
        const result = await signUp({ email, firstName, lastName, password });
        setSignupVerificationPending(true);
        setSignupCode('');
        setServerInfo(result?.message || `We sent a verification code to ${email.trim()}.`);
        return;
      }

      const user = await login({ email, password });

      await onSuccess?.(user);
    } catch (err) {
      setServerError(err?.response?.data?.error || err?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <form className="form" onSubmit={handleSubmit} noValidate>
        <h1 className="title">
          {isSignUp ? (signupVerificationPending ? 'Verify Email' : 'Sign Up') : 'Login'}
        </h1>

        {isSignUp && signupVerificationPending ? (
          <>
            <p className="subtitle" style={{ borderBottom: 'none', marginTop: 0 }}>
              Enter the code we sent to <strong>{formData.email.trim()}</strong>.
            </p>
            <Input
              name="signupCode"
              type="text"
              placeholder="Verification code"
              value={signupCode}
              onChange={(e) => {
                setSignupCode(e.target.value.replace(/[^0-9]/g, ''));
                setFieldErrors(errors => ({ ...errors, signupCode: '' }));
                setServerError('');
                setServerInfo('');
              }}
              required
              autoComplete="one-time-code"
              error={!!fieldErrors.signupCode}
              errorText={fieldErrors.signupCode}
            />
            <button
              type="button"
              className="link-inline"
              onClick={resetSignupVerification}
              disabled={loading}
              style={{ alignSelf: 'center', marginBottom: '0.75rem' }}
            >
              Edit signup details
            </button>
          </>
        ) : isSignUp && (
          <>
            <Input
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => setVal('firstName', e.target.value)}
              required
              autoComplete="given-name"
              error={!!fieldErrors.firstName}
              errorText={fieldErrors.firstName}
            />
            <Input
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) => setVal('lastName', e.target.value)}
              required
              autoComplete="family-name"
              error={!!fieldErrors.lastName}
              errorText={fieldErrors.lastName}
            />
          </>
        )}

        {!signupVerificationPending && (
          <Input
            name="email"
            type="email"
            placeholder="Email address"
            value={formData.email}
            onChange={(e) => setVal('email', e.target.value)}
            required
            autoComplete="email"
            error={!!fieldErrors.email}
            errorText={fieldErrors.email}
          />
        )}

        {!signupVerificationPending && (
          <Input
            name="password"
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => setVal('password', e.target.value)}
            required
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            minLength={8}
            error={!!fieldErrors.password}
            errorText={fieldErrors.password}
          />
        )}

        {!isSignUp && (
          <div className="forgot-wrap">
            <button
              type="button"
              className="link-inline"
              onClick={() => navigate('/forgot-password')}
            >
              Forgot your password?
            </button>
          </div>
        )}

        {isSignUp && !signupVerificationPending && (
          <Input
            name="confirmPassword"
            type="password"
            placeholder="Confirm Password"
            value={formData.confirmPassword}
            onChange={(e) => setVal('confirmPassword', e.target.value)}
            required
            autoComplete="new-password"
            error={!!fieldErrors.confirmPassword}
            errorText={fieldErrors.confirmPassword}
          />
        )}

        {serverError && (
          <div className="form-error" role="alert">
            <FaExclamationCircle className="error-icon" />
            <span className="error-text">{serverError}</span>
          </div>
        )}

        {serverInfo && (
          <div className="form-info" role="status">
            <span className="info-text">{serverInfo}</span>
          </div>
        )}

        <button type="submit" className="submitButton" disabled={loading || (isSignUp && cfg && !signupsOpen)}>
          {loading
            ? (isSignUp ? (signupVerificationPending ? 'Verifying…' : 'Sending Code…') : 'Logging In…')
            : (isSignUp ? (signupVerificationPending ? 'Verify Email' : 'Sign Up') : 'Login')}
        </button>


        {!signupVerificationPending && (
          <>
            <p className="separator">
              or {isSignUp ? 'sign up' : 'log in'} with
            </p>
            <div className="socialContainer">
              <button
                type="button"
                className="googleButton"
                onClick={onGoogleSignIn}
                disabled={loading}
              >
                <img src={googleIcon} className="googleSvgIcon" alt="Google" />
                <span>{isSignUp ? 'Sign Up with Google' : 'Login with Google'}</span>
              </button>

              <button
                type="button"
                className="microsoftButton"
                onClick={onMicrosoftSignIn}
                disabled={loading}
              >
                <img src={microsoftIcon} className="googleSvgIcon" alt="Microsoft" />
                <span>{isSignUp ? 'Sign Up with Microsoft' : 'Login with Microsoft'}</span>
              </button>
            </div>
          </>
        )}
        

        <p className="toggleText">
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}
          <span
            className="toggleLink"
            onClick={() => {
              setServerError('');
              setServerInfo('');
              setFieldErrors({});
              setSignupVerificationPending(false);
              setSignupCode('');
              setIsSignUp(x => !x);
            }}
          >
            {isSignUp ? 'Login' : 'Sign Up'}
          </span>
        </p>

        {isSignUp && (
          <p className="subtitle" style={{ borderBottom: 'none', marginTop: '0.75rem', fontSize: '.80rem' }}>
            By continuing, you agree to Telome LLC’s{' '}
            <button
              type="button"
              className="link-inline"
              onClick={() => navigate('/terms')}
              aria-label="View Terms"
            >
              Terms
            </button>{' '}
            &amp;{' '}
            <button
              type="button"
              className="link-inline"
              onClick={() => navigate('/privacy-policy')}
              aria-label="View Privacy Policy"
            >
              Privacy Policy
            </button>.
          </p>
        )}
      </form>
    </div>
  );
}
